import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import PropTypes from 'prop-types'
import moment from 'moment'
import { cloneDeep, isEqual } from 'lodash'

import InputDateField from '../../components/fields/inputdatefield/InputDateField'
import KpiPerformance from '../../components/kpiStatistics/kpiPerformance/KpiPerformance'
import KpiTrends from '../../components/kpiStatistics/kpiTrends/KpiTrends'
import KpiAdherence from '../../components/kpiStatistics/kpiAdherence/KpiAdherence'
import { getQuayConfig } from '../../utils/kpiUtils'
import { canUseFeature } from '../../utils/userPermissions'
import { USER_FEATURES } from '../../js/constants'
import { reportPageView } from '../../utils/analyticsUtils'
import './Statistics.scss'

/**
 * Checks if a DateTime field contains a valid date
 * @param dateToValidate
 * @returns {boolean}
 */
const validateDate = dateToValidate => {
  return typeof dateToValidate === 'number' && dateToValidate.toString().length === 13
}

// show statistics for the past 6 months
const defaultStopDate = moment().valueOf()
const defaultStartDate = moment().subtract(6, 'month').valueOf()

/**
 * Date validity function for Date picker. Restricts selection to the past
 * @param currentDate Current date in Date picker
 * @returns {boolean} true if date is valid
 */
const validDate = currentDate => currentDate.isBefore(moment())
class Statistics extends Component {
  constructor(props) {
    super(props)
    this.state = {
      formFields: {
        stopDate: defaultStopDate,
        startDate: defaultStartDate
      },
      selection: {
        stopDate: defaultStopDate,
        startDate: defaultStartDate
      },
      productColors: {},
      productLoadSpeeds: {},
      stationColors: {}
    }
  }

  render() {
    const { startDate, stopDate } = this.state.formFields

    if (!canUseFeature(USER_FEATURES.KPI, this.props.login.auth.userSettings)) {
      return (
        <div className="statistics-page main">
          <div className="container not-allowed">
            <div>You&#39;re not allowed to view this page</div>
          </div>
        </div>
      )
    }

    return (
      <div className="statistics-page main">
        <div className="content-wrapper">
          <div className="content">
            <div className="header-and-filters">
              <h2>Statistics</h2>
              <div className="selection-menu">
                <InputDateField
                  name={'From'}
                  fieldName={'startDate'}
                  fieldValue={startDate}
                  error={startDate ? !validateDate(startDate) || stopDate <= startDate : false}
                  required={true}
                  hideTime={true}
                  onChange={this.onFormChange}
                  validDateFn={validDate}
                />

                <InputDateField
                  name={'To'}
                  fieldName={'stopDate'}
                  fieldValue={stopDate}
                  error={stopDate ? !validateDate(stopDate) || stopDate <= startDate : false}
                  required={true}
                  hideTime={true}
                  onChange={this.onFormChange}
                  validDateFn={validDate}
                />

                <button
                  className="btn"
                  onClick={this.doSearch}
                  disabled={
                    !validateDate(this.state.formFields.startDate) ||
                    !validateDate(this.state.formFields.stopDate) ||
                    stopDate <= startDate
                  }
                >
                  View KPI
                </button>
              </div>
            </div>

            {this.state.selection && (
              <Tabs>
                <TabList>
                  <Tab>
                    <i className="fas fa-chart-line" /> Trends
                  </Tab>
                  <Tab>Performance</Tab>
                  <Tab>Planning Adherence</Tab>
                </TabList>
                <TabPanel>
                  <KpiTrends
                    customFetch={this.props.customFetch}
                    authToken={this.props.login.auth.token}
                    selection={this.state.selection}
                    productColors={this.state.productColors}
                    terminalId={this.props.login.auth.terminalId}
                  />
                </TabPanel>

                <TabPanel>
                  <KpiPerformance
                    customFetch={this.props.customFetch}
                    authToken={this.props.login.auth.token}
                    selection={this.state.selection}
                    stationColors={this.state.stationColors}
                    productColors={this.state.productColors}
                    productLoadSpeeds={this.state.productLoadSpeeds}
                    terminalId={this.props.login.auth.terminalId}
                  />
                </TabPanel>
                <TabPanel>
                  <KpiAdherence
                    customFetch={this.props.customFetch}
                    authToken={this.props.login.auth.token}
                    productColors={this.state.productColors}
                    selection={this.state.selection}
                    terminalId={this.props.login.auth.terminalId}
                  />
                </TabPanel>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    )
  }

  componentDidMount() {
    this.setColorsAndLoadSpeeds(this.props.app.present.config.quays)
    reportPageView('/statistics/kpi')
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.app.present.config.quays, prevProps.app.present.config.quays)) {
      this.setColorsAndLoadSpeeds(this.props.app.present.config.quays)
    }
  }

  setColorsAndLoadSpeeds(quays) {
    const { productColors, stationColors, productLoadSpeeds } = getQuayConfig(quays)

    this.setState({
      productColors,
      productLoadSpeeds,
      stationColors
    })
  }

  onFormChange = (value, field) => {
    if (validateDate(value)) {
      const updatedFormFields = cloneDeep(this.state.formFields)
      updatedFormFields[field] = value
      this.setState({ formFields: updatedFormFields })
    }
  }

  doSearch = evt => {
    evt.preventDefault()
    this.setState({
      selection: {
        startDate: this.state.formFields.startDate,
        stopDate: this.state.formFields.stopDate
      }
    })
  }
}

Statistics.propTypes = {
  authToken: PropTypes.string,
  customFetch: PropTypes.func.isRequired,
  login: PropTypes.object.isRequired
}

export default connect(state => state)(Statistics)
